import React, { FC, PropsWithChildren, useCallback, useMemo } from 'react';

import { useAssigneePicker, ItemWithGroup } from '@adsk/alloy-assignee-picker';
import { useUsers, Role, Company } from '@adsk/alloy-provider';
import Dropdown, {
  MultiAutocompleteTokenControl,
} from '@adsk/alloy-react-dropdown';
import FormField from '@adsk/alloy-react-form-field';
import { useLocale } from '@adsk/alloy-react-locale';
import Portal from '@adsk/alloy-react-portal';

interface AssigneePickerFilterProps {
  label: React.ReactNode;
  onChange: (items?: ItemWithGroup[]) => void;
}

interface EntityData {
  autodeskId?: string;
  roleIds?: string[];
  memberGroupId?: string;
  isLoading?: boolean;
  name?: string;
  company?: string;
}

const AssigneePickerFilter: FC<
  PropsWithChildren<AssigneePickerFilterProps>
> = ({ label, onChange }) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const users = useUsers();
  const usersData = users?.data;

  function filterUserRoles(usersData: EntityData[]) {
    const userRoles: string[] = [];
    usersData.forEach((user) => {
      const roleIds = user.roleIds || [];
      userRoles.push(...roleIds);
    });
    return userRoles;
  }

  const rolesWithMembers = useMemo(
    () => filterUserRoles(usersData),
    [usersData],
  );

  // filter functions
  const roleFilter = useCallback(
    (r: Role) => {
      return rolesWithMembers.includes(r.id);
    },
    [rolesWithMembers],
  );
  const companyFilter = useCallback((c: Company) => {
    return c.memberCount > 0;
  }, []);

  const { dropdownProps } = useAssigneePicker({
    optionsFilter: {
      company: companyFilter,
      role: roleFilter,
    },
  });

  return (
    <FormField
      style={{ marginBottom: 20 }}
      label={label}
      labelVariant={FormField.LABEL_VARIANTS.TOP}
    >
      <Dropdown<ItemWithGroup>
        {...dropdownProps}
        isAutocomplete
        isMultiple
        placeholder={T.translate(
          'SELECT_MEMBERS_ROLES_AND_COMPANIES',
        )?.toString()}
        onSelectedItemsChange={({ selectedItems }) => onChange(selectedItems)}
        renderControl={(props) => <MultiAutocompleteTokenControl {...props} />}
        renderMenu={(menuProps, virtualProps) => (
          <Portal container={document.body}>
            {dropdownProps.renderMenu(menuProps, virtualProps)}
          </Portal>
        )}
        style={{ width: '100%' }}
      />
    </FormField>
  );
};

AssigneePickerFilter.displayName = 'AssigneePickerSubmittalsFilter';

export default AssigneePickerFilter;
